/** @format */

import "../polyfill.ts";

const pause = function (duration) {
    return new Promise((resolve) => setTimeout(resolve, duration));
};

export function encodeFormData(data) {
    return Object.keys(data)
        .map(
            (key) =>
                encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
        )
        .join("&");
}

export class NotOkResponseError extends Error {
    name: string;
    status: any;
    response: any;
    constructor(response) {
        let msg = `Bad response from '${response.url}': ${response.status}`;
        super(msg);
        this.name = "NotOkResponseError";
        this.response = response;
        this.status = response.status;
    }
}

class BadGatewayError extends NotOkResponseError {}

export class NoResponseError extends Error {
    name: string;
    response: any;
    constructor(response) {
        let msg = `No response from '${response.url}': retried too many times.`;
        super(msg);
        this.name = "NoResponseError";
        this.response = response;
    }
}

export interface RefetchOptions extends RequestInit {
    timeout?: number;
    attempts?: number;
    delay?: number;
    delay_extension?: number;
}

export async function refetch(url, options: RefetchOptions = {}) {
    /**
     * Enhanced fetch with automatic retry logic for transient errors.
     *
     * Automatically retries on:
     * - Network errors (DNS failures, connection refused, etc.)
     * - HTTP 502/503/504 status codes (gateway/server errors)
     * - HTTP 500 without valid JSON response (infrastructure failures)
     *
     * Does NOT retry on:
     * - HTTP 500 with valid JSON (application errors)
     * - Request timeouts (AbortError)
     * - Client errors (4xx status codes)
     *
     * Options:
     * - timeout: Request timeout in ms before aborting (default: 10000)
     * - attempts: Maximum retry attempts (default: 10)
     * - delay: Initial delay between retries in ms (default: 1000)
     * - delay_extension: Additional delay added each retry in ms (default: 1000)
     *
     * Implements exponential backoff: delay increases by delay_extension each attempt.
     * Example: 1s, 2s, 3s, 4s... between retries.
     */

    options = Object.assign(
        {
            timeout: 10000,
            attempts: 10,
            delay: 1000,
            delay_extension: 1000,
        },
        options
    );

    for (let i = 1; i <= options.attempts; i++) {
        const controller = new AbortController();
        options.signal = controller.signal;

        let abortTimeout;

        try {
            if (options.timeout) {
                abortTimeout = setTimeout(() => {
                    controller.abort();
                }, options.timeout);
            }

            let response = await fetch(url, options);
            let retry = false;
            let clone = response.clone();
            let hasValidJson = false;

            try {
                let responseJson = await clone.json();
                hasValidJson = true;

                if (responseJson && responseJson["retry"]) {
                    retry = true;
                }
            } catch (err) {
                // ignore - not valid JSON
            }

            if (
                [502, 503, 504].includes(response.status) ||
                (response.status === 500 && !hasValidJson)
            ) {
                clearTimeout(abortTimeout);
                throw new BadGatewayError(response);
            }

            if (!retry) {
                clearTimeout(abortTimeout);
                return response;
            } else if (i === options.attempts) {
                clearTimeout(abortTimeout);
                throw new NoResponseError(response);
            }
        } catch (err) {
            clearTimeout(abortTimeout);

            // Don't retry on timeout errors or final attempt
            if (err.name === "AbortError" || i === options.attempts) {
                throw err;
            }
            // Otherwise, continue to retry (BadGatewayError, network errors, etc.)
        }

        if (i !== options.attempts) {
            await pause(options.delay);
            options.delay += options.delay_extension;
        }
    }
}
